/**
 * PdfStrip — renders all pages of a base64-encoded PDF as grid thumbnails.
 *
 * Ported from AdminTemplates.tsx `PdfGrid` component (lines 618–718).
 * Enhanced with:
 *   • activeSlideIndex — highlighted border on the active thumbnail
 *   • onSlideClick     — click thumbnail to navigate to single view
 *   • onRemove         — optional ❌ button per thumbnail (D1.3)
 *   • slideCountMin    — disables remove when at the minimum count
 *   • Responsive: auto-fill ≥200px columns (2-col on 375px mobile, auto on desktop)
 *
 * Used by DistillPageV2 Step 4 grid view.
 */

import React, { useState, useEffect } from "react";

const THUMB_COL_MIN = 200; // px — 2 columns on 375px phone, auto-fill wider

const PDF_WORKER_URL = (version: string) =>
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;

export interface PdfStripProps {
  base64Pdf: string;
  activeSlideIndex: number;           // 0-based
  onSlideClick: (index: number) => void;
  onRemove?: (index: number) => void; // D1.3 — optional remove button
  slideCountMin?: number;             // D1.3 — disable remove when at min
  /** Pre-rendered thumbnail data-URLs from a previous parse. When non-empty,
   *  the component skips re-parsing the PDF and displays them immediately. */
  preRenderedUrls?: string[];
  /** Called once after a fresh parse completes so the parent can cache the URLs. */
  onThumbsReady?: (urls: string[]) => void;
}

const PdfStrip: React.FC<PdfStripProps> = ({
  base64Pdf,
  activeSlideIndex,
  onSlideClick,
  onRemove,
  slideCountMin = 1,
  preRenderedUrls,
  onThumbsReady,
}) => {
  const [pageDataUrls, setPageDataUrls] = useState<string[]>([]);
  const [loading, setLoading]           = useState(false);

  useEffect(() => {
    if (!base64Pdf) { setPageDataUrls([]); return; }

    // If the parent has already parsed this PDF and cached the thumbnail URLs,
    // use them directly — skip the expensive pdfjs parse entirely.
    if (preRenderedUrls && preRenderedUrls.length > 0) {
      setPageDataUrls(preRenderedUrls);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setPageDataUrls([]); // clear stale thumbnails while reloading

    (async () => {
      try {
        const pdfjsLib = await import("pdfjs-dist");
        (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
          PDF_WORKER_URL((pdfjsLib as any).version);

        const bytes = Uint8Array.from(atob(base64Pdf), c => c.charCodeAt(0));
        const doc   = await (pdfjsLib as any).getDocument({ data: bytes }).promise;

        // ── Sharp thumbnails: render-to-physical-pixels formula ──────────────
        //
        // The grid uses `minmax(200px, 1fr)`, so on V2's narrow main column
        // (max 660px wide per sharedStyles.mainStyle) thumbnails can stretch
        // to a single 660px-wide column. On retina (DPR=2) that's 1320 physical
        // pixels. The PNG buffer must be AT LEAST that big to avoid upscaling.
        //
        // Direct formula: render to `MAX_DISPLAY_WIDTH × DPR` physical pixels.
        // No abstract "oversample" multipliers — the intent is stated directly.
        //
        //   PDF source (1080×1350) ──┐
        //                            ├─→ scale = (720 × DPR) / 1080
        //   Target buffer width ─────┘   On DPR=2: scale = 1.333 → buffer 1440px
        //                                On DPR=1: scale = 0.667 → buffer  720px
        //
        // Result on retina: 1440px buffer for a 660px CSS img = 1.09× downscale → crisp.
        // Result on Windows: 720px buffer for a 660px CSS img = 1.09× downscale → crisp.
        //
        // Capped at scale=2 because rendering above 2× native PDF resolution gains
        // nothing visible (PDF source ITSELF is the upper limit of detail).
        const dpr               = window.devicePixelRatio || 1;
        const MAX_DISPLAY_WIDTH = 720;   // V2 main column max 660px + headroom
        const urls: string[]    = [];

        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page   = await doc.getPage(i);
          const baseVp = page.getViewport({ scale: 1 });
          // Use the SHORTER dimension as the target axis so portrait + landscape
          // PDFs both end up with their longest edge at MAX_DISPLAY_WIDTH × DPR.
          const desired = (MAX_DISPLAY_WIDTH / Math.min(baseVp.width, baseVp.height)) * dpr;
          const scale   = Math.min(desired, 2); // never above 2× native — wasted pixels
          const vp      = page.getViewport({ scale });

          const offscreen  = document.createElement("canvas");
          offscreen.width  = vp.width;
          offscreen.height = vp.height;
          await page.render({
            canvasContext: offscreen.getContext("2d")!,
            viewport: vp,
          }).promise;
          urls.push(offscreen.toDataURL("image/png"));
        }

        if (!cancelled) {
          setPageDataUrls(urls);
          onThumbsReady?.(urls); // notify parent so it can cache for instant restore
        }
      } catch (e) {
        console.error("[PdfStrip] render error", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [base64Pdf]); // eslint-disable-line react-hooks/exhaustive-deps
  // preRenderedUrls intentionally excluded: the effect reads the latest value via
  // closure at the moment base64Pdf changes. Adding it to deps would cause a re-run
  // every time the parent stores new thumbnails, creating an infinite loop.

  if (loading && pageDataUrls.length === 0) {
    return (
      <div style={{
        padding: "40px 0", textAlign: "center",
        color: "#aaa", fontSize: "13px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: "12px",
      }}>
        <div style={{
          width: "20px", height: "20px", borderRadius: "50%",
          border: "2px solid rgba(91,79,232,0.2)", borderTopColor: "#5B4FE8",
          animation: "spin 0.8s linear infinite",
        }} />
        Rendering thumbnails…
      </div>
    );
  }

  const canRemove = !!onRemove && pageDataUrls.length > slideCountMin;

  return (
    <>
      <style>{`
        .pdf-strip-remove-btn {
          position: absolute; top: 6px; right: 6px;
          width: 20px; height: 20px; border-radius: 50%;
          background: rgba(0,0,0,0.38); color: rgba(255,255,255,0.9);
          border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 9px; font-weight: 700; line-height: 1;
          z-index: 2;
          transition: opacity 0.15s, background 0.15s;
        }
        .pdf-strip-remove-btn::before {
          content: ''; position: absolute;
          /* 44×44 touch target */
          top: -12px; right: -12px; bottom: -12px; left: -12px;
        }
        .pdf-strip-remove-btn:hover:not(:disabled) {
          background: rgba(0,0,0,0.62);
        }
        .pdf-strip-remove-btn:disabled {
          background: rgba(0,0,0,0.18); color: rgba(255,255,255,0.5); cursor: not-allowed;
        }
        /* On pointer devices: hide until the thumbnail is hovered */
        @media (hover: hover) {
          .pdf-strip-thumb:not(:hover) .pdf-strip-remove-btn { opacity: 0; }
        }
        .pdf-strip-thumb-img {
          transition: border-color 0.15s, box-shadow 0.15s;
        }
      `}</style>

      <div style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${THUMB_COL_MIN}px, 1fr))`,
        gap: "12px",
        padding: "16px 0 8px",
        width: "100%",
      }}>
        {pageDataUrls.map((url, i) => {
          const isActive = i === activeSlideIndex;
          return (
            <div
              key={i}
              className="pdf-strip-thumb"
              style={{ position: "relative", cursor: "pointer" }}
              onClick={() => onSlideClick(i)}
            >
              {/* Remove button (D1.3) */}
              {onRemove && (
                <button
                  className="pdf-strip-remove-btn"
                  title={canRemove ? `Remove slide ${i + 1}` : "Minimum slide count reached"}
                  disabled={!canRemove}
                  aria-label={`Remove slide ${i + 1}`}
                  onClick={e => {
                    e.stopPropagation();
                    if (canRemove) onRemove(i);
                  }}
                >
                  ✕
                </button>
              )}

              <img
                className="pdf-strip-thumb-img"
                src={url}
                alt={`Slide ${i + 1}`}
                style={{
                  width: "100%", height: "auto", display: "block",
                  borderRadius: "6px",
                  border: isActive ? "2.5px solid #5B4FE8" : "1.5px solid #E4E4E0",
                  boxShadow: isActive
                    ? "0 0 0 3px rgba(91,79,232,0.15)"
                    : "0 2px 6px rgba(0,0,0,0.06)",
                }}
              />
              <span style={{
                display: "block", textAlign: "center",
                fontSize: "11px", marginTop: "5px",
                color: isActive ? "#5B4FE8" : "#aaa",
                fontWeight: isActive ? 700 : 400,
              }}>
                {i + 1}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default React.memo(PdfStrip);
